import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Settings,
  Bot,
  Palette,
  Calendar,
  Phone,
  BarChart3,
  FileText,
  Loader2,
  Plus,
  X,
  Save,
  Target,
  Info,
  Cpu,
  MessageSquare,
  Activity
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { AgentConfigShell, type AgentSectionDef } from '@/components/ai/shell/AgentConfigShell';
import { agentProviderForEdit, fetchAgentLlmDefault } from '@/lib/agent-llm-default';
import { LLMProviderConfig, type LLMConfig, inferProviderFromModelFrontend } from '@/components/ai/LLMProviderConfig';
import PreCallHooksSection from '@/components/ai/PreCallHooksSection';
import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import { isTenantLimitHandled } from '@/lib/tenantLimits';
import { fetchChatModelOptions } from '@/hooks/useChatModelOptions';
import { KnowledgeBaseManager } from '@/components/ai/KnowledgeBaseManager';
import IdentitySelector from '@/components/ai/IdentitySelector';
import {
  KbModelChangeGuardDialog,
  useKbModelChangeGuard,
  type KbGuardedPerformSaveArgs,
} from '@/components/ai/KbModelChangeGuardDialog';
import { useAuth } from '@/contexts/AuthContext';
import type { PreCallHooksConfig } from '@/components/ai/PreCallHooksSection';
import type { LLMProviderExtended } from '@/constants/ai-providers';


interface ChatbotAgent {
  id?: string;
  ai_agent_id?: string;
  acts_as_user_ids?: string[];
  allowed_sender_ids?: string[] | null;
  name: string;
  description?: string;
  llm_provider: string;
  llm_config?: LLMConfig;
  model: string;
  temperature: number;
  system_prompt: string;
  pre_call_hooks?: PreCallHooksConfig;
  rag_enabled: boolean;
  rag_inferential_enabled: boolean;
  rag_synthesis_mode: string;
  context_memory_enabled: boolean;
  semantic_memory_enabled: boolean;
  // Task #1984 round B — riferimenti endpoint/modello custom.
  custom_endpoint_id?: string | null;
  custom_model_id?: string | null;
  widget_config: {
    primary_color?: string;
    secondary_color?: string;
    position?: 'bottom-right' | 'bottom-left';
    bubble_size?: number;
    avatar_url?: string;
    welcome_message?: string;
    offline_message?: string;
    header_text?: string;
  };
  availability_config: {
    always_available?: boolean;
    schedule?: Array<{ day: number; start: string; end: string }>;
    timezone?: string;
  };
  click_to_call_enabled: boolean;
  click_to_call_config: {
    mode?: 'webrtc' | 'callback';
    flow_type?: 'human' | 'ai_agent';
    destination_type?: 'extension' | 'queue' | 'ring_group';
    destination_id?: string;
    trunk_id?: string;
    ai_agent_id?: string;
  };
  lead_capture_enabled: boolean;
  default_stage_id?: string;
  quick_replies: Array<{ text: string; action?: string }>;
  is_active: boolean;
  transcription_enabled: boolean;
  sentiment_enabled: boolean;
  // Task #1316 — stato fallback modello risolto lato backend (opzionale sul wire).
  model_fallback_active?: boolean;
  model_fallback_original?: string;
  model_fallback_resolved?: string;
}

interface Campaign {
  id: string;
  name: string;
}

interface ChatbotConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chatbot?: ChatbotAgent | null;
  onSave: () => void;
  extensions?: Array<{ id: string; extension_number: string; display_name: string }>;
  queues?: Array<{ id: string; name: string; extension: string }>;
  trunks?: Array<{ id: string; name: string; gateway_name?: string }>;
  voiceAgents?: Array<{ id: string; name: string; description?: string | null; is_active?: boolean; provider?: string; provider_name?: string; sip_enabled?: boolean }>;
  campaigns?: Campaign[];
}

const defaultChatbot: ChatbotAgent = {
  name: '',
  description: '',
  llm_provider: 'openai',
  llm_config: {},
  model: 'gpt-5.4-mini',
  temperature: 0.7,
  system_prompt: 'Sei un assistente virtuale cordiale e professionale. Rispondi alle domande in modo chiaro e conciso.',
  rag_enabled: true,
  rag_inferential_enabled: false,
  rag_synthesis_mode: 'native',
  context_memory_enabled: false,
  semantic_memory_enabled: false,
  custom_endpoint_id: null,
  custom_model_id: null,
  widget_config: {
    primary_color: '#3b82f6',
    secondary_color: '#1e40af',
    position: 'bottom-right',
    bubble_size: 60,
    welcome_message: 'Ciao! Come posso aiutarti?',
    offline_message: 'Al momento non siamo disponibili. Lascia un messaggio!',
    header_text: 'Assistente Virtuale'
  },
  availability_config: {
    always_available: true,
    schedule: [],
    timezone: 'Europe/Rome'
  },
  click_to_call_enabled: false,
  click_to_call_config: {
    mode: 'callback',
    flow_type: 'human',
    destination_type: 'queue'
  },
  lead_capture_enabled: true,
  quick_replies: [],
  is_active: true,
  transcription_enabled: true,
  sentiment_enabled: false
};

export default function ChatbotConfigModal({
  open,
  onOpenChange,
  chatbot,
  onSave,
  extensions = [],
  queues = [],
  trunks = [],
  voiceAgents = [],
  campaigns = []
}: ChatbotConfigModalProps) {
  const { toast } = useToast();
  const { user: authUser } = useAuth();
  const isAdminUser = authUser?.role === 'admin' || authUser?.role === 'super_admin';
  const [activeTab, setActiveTab] = useState('identity');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<ChatbotAgent>(defaultChatbot);
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  const [newQuickReply, setNewQuickReply] = useState('');

  const [dataLoaded, setDataLoaded] = useState(false);
  const [savedModel, setSavedModel] = useState<string | undefined>(undefined);
  const [fallbackInfo, setFallbackInfo] = useState<{ active: boolean; originalModel?: string; resolvedModel?: string }>({ active: false });
  const llmConfigByProvider = useRef<Record<string, LLMConfig>>({});
  const modelWarningShown = useRef(false);
  const isEdit = !!chatbot?.id;

  // Task #1307 / #1316 — guard cambio chat model con doc multimodali Gemini.
  const kbGuard = useKbModelChangeGuard();


  const loadChatbotCampaigns = async (chatbotId: string) => {
    try {
      const res = await apiClient.get<{ data?: { campaigns?: Campaign[] }; campaigns?: Campaign[] }>(`/chatbot/agents/${chatbotId}`);
      const envelope = res.data;
      const data = envelope?.data ?? envelope;
      if (data?.campaigns) {
        setSelectedCampaigns(data.campaigns.map((c) => c.id));
      } else {
        setSelectedCampaigns([]);
      }
    } catch (error) {
      console.error('Error loading chatbot campaigns:', error);
      setSelectedCampaigns([]);
    }
  };

  useEffect(() => {
    if (open) {
      setDataLoaded(false);
      llmConfigByProvider.current = {};
      setFallbackInfo({ active: false });
      setSavedModel(undefined);
      if (chatbot) {
        const provider = agentProviderForEdit(chatbot.llm_provider, chatbot.model);
        setFormData({
          ...defaultChatbot,
          ...chatbot,
          llm_provider: provider,
          widget_config: { ...defaultChatbot.widget_config, ...chatbot.widget_config },
          availability_config: { ...defaultChatbot.availability_config, ...chatbot.availability_config },
          click_to_call_config: { ...defaultChatbot.click_to_call_config, ...chatbot.click_to_call_config }
        });
        setSavedModel(chatbot.model);
        if (chatbot.llm_config && Object.keys(chatbot.llm_config).length > 0) {
          llmConfigByProvider.current[provider] = chatbot.llm_config;
        }
        if (chatbot.model_fallback_active) {
          setFallbackInfo({ active: true, originalModel: chatbot.model_fallback_original, resolvedModel: chatbot.model_fallback_resolved });
        }
        if (chatbot.id) {
          loadChatbotCampaigns(chatbot.id);
        }
        setDataLoaded(true);
      } else {
        setFormData(defaultChatbot);
        setSelectedCampaigns([]);
        setDataLoaded(true);
        // Nuovo agente: parte dal modello predefinito degli agenti del tenant.
        void fetchAgentLlmDefault().then((llm) => {
          if (!llm) return;
          setFormData((prev) => ({
            ...prev,
            llm_provider: llm.llm_provider,
            model: llm.model,
            custom_endpoint_id: llm.custom_endpoint_id,
            custom_model_id: llm.custom_model_id,
          }));
        });
      }
      setActiveTab('identity');
    }
  }, [open, chatbot]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Errore', description: 'Il nome è obbligatorio', variant: 'destructive' });
      return;
    }
    if (formData.click_to_call_enabled) {
      if (!formData.click_to_call_config.trunk_id) {
        toast({ title: 'Errore', description: 'Seleziona un trunk SIP per il click-to-call', variant: 'destructive' });
        setActiveTab('call');
        return;
      }
      const flowType = formData.click_to_call_config.flow_type || 'human';
      if (flowType === 'human' && !formData.click_to_call_config.destination_id) {
        toast({ title: 'Errore', description: 'Seleziona una destinazione (coda o interno)', variant: 'destructive' });
        setActiveTab('call');
        return;
      }
      if (flowType === 'ai_agent' && !formData.click_to_call_config.ai_agent_id) {
        toast({ title: 'Errore', description: 'Seleziona un Agent AI Voice', variant: 'destructive' });
        setActiveTab('call');
        return;
      }
    }

    // Task #1984 round B — gate provider custom: richiede endpoint UUID.
    if (formData.llm_provider === 'custom') {
      if (!formData.custom_endpoint_id) {
        toast({ title: 'Errore', description: 'Seleziona un endpoint custom prima di salvare.', variant: 'destructive' });
        setActiveTab('model');
        return;
      }
      if (!formData.model) {
        toast({ title: 'Errore', description: 'Seleziona un modello custom prima di salvare.', variant: 'destructive' });
        setActiveTab('model');
        return;
      }
    }

    // Per provider 'custom' NON applichiamo inferProviderFromModelFrontend
    // (i model id custom sono arbitrari, es. `qwen2.5-72b-instruct`).
    const inferredProvider = formData.llm_provider === 'custom'
      ? 'custom'
      : inferProviderFromModelFrontend(formData.model);
    if (formData.llm_provider && formData.llm_provider !== inferredProvider) {
      toast({ title: 'Attenzione', description: `Il modello "${formData.model}" non corrisponde al provider "${formData.llm_provider}". Il provider verrà corretto automaticamente in "${inferredProvider}".` });
    }

    if (formData.llm_provider !== 'custom' && !modelWarningShown.current) {
      let modelConfirmedAvailable = true;
      let recommendedHint = '';
      try {
        const payload = await fetchChatModelOptions(inferredProvider as LLMProviderExtended);
        if (payload.models.length > 0) {
          modelConfirmedAvailable = payload.models.some(
            (m) => m.id === formData.model && m.status !== 'deprecated',
          );
          const recommended = payload.models.find((m) => m.status !== 'deprecated');
          if (recommended) recommendedHint = ` Modello consigliato: ${recommended.displayName || recommended.name || recommended.id}.`;
        }
      } catch {
        modelConfirmedAvailable = true;
      }
      if (!modelConfirmedAvailable) {
        modelWarningShown.current = true;
        toast({ title: 'Modello non disponibile', description: `Il modello "${formData.model}" non risulta disponibile per ${inferredProvider}.${recommendedHint} Premi di nuovo "Salva" per confermare comunque.`, variant: 'destructive' });
        return;
      }
    }
    modelWarningShown.current = false;

    await kbGuard.runGuardedSave({
      agentId: isEdit ? chatbot?.id ?? null : null,
      newChatModel: formData.model,
      performSave: performSave,
    });
  };

  async function performSave(opts: KbGuardedPerformSaveArgs) {
    const inferredProvider = formData.llm_provider === 'custom'
      ? 'custom'
      : inferProviderFromModelFrontend(formData.model);
    setIsSaving(true);
    try {
      const savePayload = {
        ...formData,
        llm_provider: inferredProvider,
        // Task #1984 round B — invia gli UUID custom solo se provider==='custom'.
        custom_endpoint_id: inferredProvider === 'custom' ? (formData.custom_endpoint_id || null) : null,
        custom_model_id: inferredProvider === 'custom' ? (formData.custom_model_id || null) : null,
        campaign_ids: selectedCampaigns
      };
      // `opts.reingestDocIds` è gestito dal guard hook DOPO il PUT.
      void opts;
      if (isEdit) {
        await apiClient.put(`/chatbot/agents/${chatbot.id}`, savePayload);
        toast({ title: 'Successo', description: 'Chatbot aggiornato con successo' });
      } else {
        await apiClient.post('/chatbot/agents', savePayload);
        toast({ title: 'Successo', description: 'Chatbot creato con successo' });
      }
      onSave();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving chatbot:', error);
      // Task #1435 — limite tenant agenti AI: modal globale già aperto.
      if (isTenantLimitHandled(error)) {
        onOpenChange(false);
        return;
      }
      toast({
        title: 'Errore',
        description: getApiErrorMessage(error) || 'Errore durante il salvataggio',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addQuickReply = () => {
    if (!newQuickReply.trim()) return;
    setFormData(prev => ({
      ...prev,
      quick_replies: [...prev.quick_replies, { text: newQuickReply.trim() }]
    }));
    setNewQuickReply('');
  };

  const removeQuickReply = (index: number) => {
    setFormData(prev => ({
      ...prev,
      quick_replies: prev.quick_replies.filter((_, i) => i !== index)
    }));
  };

  const sections: AgentSectionDef[] = [
    { id: 'identity', label: 'Identità', description: 'Nome e descrizione', icon: <Bot className="h-4 w-4" /> },
    { id: 'model', label: 'Modello LLM', description: 'Provider, parametri generazione', icon: <Cpu className="h-4 w-4" /> },
    { id: 'prompt', label: 'Prompt & Hooks', description: 'Istruzioni e contesto', icon: <MessageSquare className="h-4 w-4" /> },
    { id: 'behavior', label: 'Comportamento', description: 'Memoria, trascrizione', icon: <Activity className="h-4 w-4" /> },
    { id: 'knowledge', label: 'Knowledge Base', description: isEdit ? 'Documenti e fonti' : 'Disponibile dopo il salvataggio', icon: <FileText className="h-4 w-4" /> },
    { id: 'widget', label: 'Widget', description: 'Aspetto e messaggi', icon: <Palette className="h-4 w-4" /> },
    { id: 'campaigns', label: 'CRM & Lead', description: 'Cattura lead, campagne', icon: <Target className="h-4 w-4" /> },
    { id: 'call', label: 'Click-to-Call', description: 'Chiamate dal widget', icon: <Phone className="h-4 w-4" />, isInvalid: formData.click_to_call_enabled && !formData.click_to_call_config.trunk_id },
    { id: 'hours', label: 'Orari', description: 'Disponibilità', icon: <Calendar className="h-4 w-4" /> },
  ];

  return (
    <AgentConfigShell
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Configura Chatbot' : 'Nuovo Chatbot'}
      subtitle={formData.name || undefined}
      icon={<Bot className="h-5 w-5" />}
      accentColor="pink"
      sections={sections}
      activeSectionId={activeTab}
      onSectionChange={setActiveTab}
      onSave={handleSave}
      footerRight={
        <>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isSaving} className="bg-gradient-to-r from-pink-500 to-rose-600">
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {isEdit ? 'Salva Modifiche' : 'Crea Chatbot'}
          </Button>
        </>
      }
    >
      <div>
        {activeTab === 'identity' && (
          <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome Chatbot *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Assistente Commerciale"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Input
                  value={formData.description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Chatbot per assistenza clienti e prenotazioni"
                />
              </div>

              {/* Il chatbot è un ai_agent
                  e usa lo stesso modello (acts_as_user_ids/allowed_sender_ids)
                  di PA, workflow e api-key. Default tenant-wide. */}
              <IdentitySelector
                value={{
                  acts_as_user_ids: formData.acts_as_user_ids ?? [],
                  allowed_sender_ids: formData.allowed_sender_ids ?? null,
                }}
                canTenantWide={isAdminUser}
                onChange={(next) => setFormData(prev => ({
                  ...prev,
                  acts_as_user_ids: next.acts_as_user_ids,
                  allowed_sender_ids: next.allowed_sender_ids,
                }))}
              />
          </div>
        )}

        {activeTab === 'model' && (
          <div className="space-y-4">
              <LLMProviderConfig
                provider={formData.llm_provider}
                model={formData.model}
                llmConfig={formData.llm_config || {}}
                loading={isEdit && !dataLoaded}
                savedModel={savedModel}
                agentId={formData.ai_agent_id ?? null}
                fallbackInfo={fallbackInfo}
                onAcceptFallback={() => {
                  const resolved = fallbackInfo.resolvedModel;
                  if (resolved) {
                    const resolvedProvider = inferProviderFromModelFrontend(resolved);
                    setFormData(prev => ({ ...prev, model: resolved, llm_provider: resolvedProvider }));
                    setSavedModel(resolved);
                  } else {
                    setSavedModel(formData.model);
                  }
                  setFallbackInfo({ active: false });
                }}
                onProviderChange={(provider, defaultModel) => {
                  llmConfigByProvider.current[formData.llm_provider] = formData.llm_config || {};
                  const restoredConfig = llmConfigByProvider.current[provider] || {};
                  setSavedModel(undefined);
                  setFallbackInfo({ active: false });
                  setFormData(prev => ({
                    ...prev,
                    llm_provider: provider,
                    model: defaultModel,
                    llm_config: restoredConfig,
                    // Reset refs custom quando si cambia provider.
                    custom_endpoint_id: provider === 'custom' ? prev.custom_endpoint_id : null,
                    custom_model_id: provider === 'custom' ? prev.custom_model_id : null,
                  }));
                }}
                onModelChange={(model) => { modelWarningShown.current = false; setFormData(prev => ({ ...prev, model })); }}
                onConfigChange={(config) => setFormData(prev => ({ ...prev, llm_config: config }))}
                customEndpointId={formData.custom_endpoint_id ?? null}
                customModelId={formData.custom_model_id ?? null}
                onCustomChange={(epId, mdId) => setFormData(prev => ({ ...prev, custom_endpoint_id: epId, custom_model_id: mdId }))}
              />

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Temperatura: {formData.temperature}</Label>
                  <span className="text-xs text-muted-foreground">
                    {formData.temperature < 0.3 ? 'Preciso' : formData.temperature > 0.7 ? 'Creativo' : 'Bilanciato'}
                  </span>
                </div>
                <input
                  type="range"
                  value={formData.temperature}
                  onChange={(e) => setFormData(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                  min={0}
                  max={1}
                  step={0.1}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
              </div>
          </div>
        )}

        {activeTab === 'prompt' && (
          <div className="space-y-4">
              <div className="space-y-2">
                <Label>System Prompt</Label>
                <Textarea
                  value={formData.system_prompt}
                  onChange={(e) => setFormData(prev => ({ ...prev, system_prompt: e.target.value }))}
                  rows={10}
                  placeholder="Definisci la personalità e il comportamento del chatbot..."
                />
              </div>

              <PreCallHooksSection
                hooks={formData.pre_call_hooks || { inject_datetime: true, auto_crm_lookup: true }}
                onChange={(hooks) => setFormData(prev => ({ ...prev, pre_call_hooks: hooks }))}
                agentType="chatbot"
              />
          </div>
        )}

        {activeTab === 'behavior' && (
          <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Label>Memoria Contesto</Label>
                  <p className="text-xs text-muted-foreground">Ricorda informazioni durante la sessione corrente</p>
                </div>
                <Switch
                  checked={formData.context_memory_enabled}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, context_memory_enabled: checked }))}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Label>Memoria Semantica</Label>
                  <p className="text-xs text-muted-foreground">Ricorda fatti e preferenze tra sessioni diverse</p>
                </div>
                <Switch
                  checked={formData.semantic_memory_enabled}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, semantic_memory_enabled: checked }))}
                />
              </div>

              <div className="space-y-2 mt-4">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4 text-purple-500" />
                  Trascrizione & Analisi
                </Label>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                  <div>
                    <p className="text-sm font-medium text-foreground">Trascrizione Conversazioni</p>
                    <p className="text-xs text-muted-foreground">Salva il testo completo di ogni conversazione</p>
                  </div>
                  <Switch
                    checked={formData.transcription_enabled}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      transcription_enabled: checked,
                      sentiment_enabled: checked ? prev.sentiment_enabled : false,
                    }))}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                  <div>
                    <p className="text-sm font-medium text-foreground">Analisi Sentiment</p>
                    <p className="text-xs text-muted-foreground">Analizza emozioni durante la conversazione</p>
                  </div>
                  <Switch
                    checked={formData.sentiment_enabled}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, sentiment_enabled: checked }))}
                    disabled={!formData.transcription_enabled}
                  />
                </div>
              </div>

          </div>
        )}

        {activeTab === 'knowledge' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg border border-border">
              <div>
                <Label>Knowledge Base RAG</Label>
                <p className="text-xs text-muted-foreground">Risposte basate sui tuoi documenti</p>
              </div>
              <Switch
                checked={formData.rag_enabled}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, rag_enabled: checked }))}
              />
            </div>

            {formData.rag_enabled && (
              <div className="flex items-center justify-between p-3 rounded-lg border border-border ml-4 border-l-2 border-blue-300">
                <div className="flex items-center gap-1.5">
                  <Label>Sintesi Nativa</Label>
                  <div className="relative group">
                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-64 p-2 bg-gray-900 text-white text-[10px] rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none">
                      <p className="font-medium mb-1">ON: L'agente ragiona direttamente sui dati recuperati dalla Knowledge Base. Più veloce, più naturale, costo inferiore.</p>
                      <p>OFF: I dati vengono prima sintetizzati da un modello ausiliario (GPT-4o-mini) e poi passati all'agente. Più controllata, utile con modelli costosi.</p>
                    </div>
                  </div>
                </div>
                <Switch
                  checked={formData.rag_synthesis_mode === 'native'}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, rag_synthesis_mode: checked ? 'native' : 'pre_synthesized', rag_inferential_enabled: !checked }))}
                />
              </div>
            )}

            {formData.rag_enabled && (
              isEdit && chatbot?.id ? (
                <KnowledgeBaseManager
                  agentId={chatbot.id}
                  agentType="chatbot"
                  onNavigateToRouting={() => setActiveTab('model')}
                  canNavigateToRouting={isAdminUser}
                />
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Salva prima il chatbot per gestire la Knowledge Base</p>
                </div>
              )
            )}
          </div>
        )}

        {activeTab === 'widget' && (
          <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Colore Primario</Label>
                  <p className="text-xs text-muted-foreground">Colore della bolla flottante e dei messaggi inviati dall'utente.</p>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={formData.widget_config.primary_color || '#3b82f6'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        widget_config: { ...prev.widget_config, primary_color: e.target.value }
                      }))}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={formData.widget_config.primary_color || '#3b82f6'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        widget_config: { ...prev.widget_config, primary_color: e.target.value }
                      }))}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Colore Secondario</Label>
                  <p className="text-xs text-muted-foreground">Sfumatura/accento dell'header e dei dettagli del widget.</p>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={formData.widget_config.secondary_color || '#1e40af'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        widget_config: { ...prev.widget_config, secondary_color: e.target.value }
                      }))}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={formData.widget_config.secondary_color || '#1e40af'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        widget_config: { ...prev.widget_config, secondary_color: e.target.value }
                      }))}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Posizione Bubble</Label>
                <p className="text-xs text-muted-foreground">Angolo dello schermo in cui compare la bolla del chatbot sul sito.</p>
                <Select
                  value={formData.widget_config.position || 'bottom-right'}
                  onValueChange={(value: 'bottom-right' | 'bottom-left') => setFormData(prev => ({
                    ...prev,
                    widget_config: { ...prev.widget_config, position: value }
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bottom-right">Basso Destra</SelectItem>
                    <SelectItem value="bottom-left">Basso Sinistra</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Titolo Header</Label>
                <p className="text-xs text-muted-foreground">Testo mostrato in alto nella finestra di chat (es. nome del servizio o team).</p>
                <Input
                  value={formData.widget_config.header_text || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    widget_config: { ...prev.widget_config, header_text: e.target.value }
                  }))}
                  placeholder="Assistente Virtuale"
                />
              </div>

              <div className="space-y-2">
                <Label>Messaggio di Benvenuto</Label>
                <p className="text-xs text-muted-foreground">Primo messaggio mostrato dal bot all'apertura della chat, prima che l'utente scriva.</p>
                <Textarea
                  value={formData.widget_config.welcome_message || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    widget_config: { ...prev.widget_config, welcome_message: e.target.value }
                  }))}
                  rows={2}
                  placeholder="Ciao! Come posso aiutarti?"
                />
              </div>

              <div className="space-y-2">
                <Label>Messaggio Offline</Label>
                <p className="text-xs text-muted-foreground">Messaggio mostrato fuori dagli orari di apertura configurati nella sezione "Orari".</p>
                <Textarea
                  value={formData.widget_config.offline_message || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    widget_config: { ...prev.widget_config, offline_message: e.target.value }
                  }))}
                  rows={2}
                  placeholder="Al momento non siamo disponibili..."
                />
              </div>

              <div className="space-y-2">
                <Label>Risposte Rapide</Label>
                <p className="text-xs text-muted-foreground">
                  Pulsanti suggeriti mostrati sopra il campo di scrittura all'apertura della chat. Cliccandoli, il testo viene inviato come se l'utente l'avesse digitato. Spariscono dopo il primo messaggio. Utili come scorciatoie (es. "Orari", "Preventivo", "Parlare con un operatore").
                </p>
                <div className="flex gap-2">
                  <Input
                    value={newQuickReply}
                    onChange={(e) => setNewQuickReply(e.target.value)}
                    placeholder="Es: Orari di apertura"
                    onKeyDown={(e) => e.key === 'Enter' && addQuickReply()}
                  />
                  <Button onClick={addQuickReply} disabled={!newQuickReply.trim()}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.quick_replies.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.quick_replies.map((reply, idx) => (
                      <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                        {reply.text}
                        <X
                          className="h-3 w-3 cursor-pointer hover:text-red-400"
                          onClick={() => removeQuickReply(idx)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
          </div>
        )}

        {activeTab === 'campaigns' && (
          <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Label>Cattura Lead</Label>
                  <p className="text-xs text-muted-foreground">Crea lead CRM dalle conversazioni</p>
                </div>
                <Switch
                  checked={formData.lead_capture_enabled}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, lead_capture_enabled: checked }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Campagne Associate</Label>
                <p className="text-xs text-muted-foreground mb-2">I lead verranno associati a queste campagne</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {campaigns.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nessuna campagna disponibile</p>
                  ) : (
                    campaigns.map((campaign) => (
                      <div
                        key={campaign.id}
                        className={`p-2 rounded border cursor-pointer transition-colors ${
                          selectedCampaigns.includes(campaign.id)
                            ? 'border-blue-500 bg-blue-500/20'
                            : 'border-border hover:border-gray-400'
                        }`}
                        onClick={() => {
                          setSelectedCampaigns(prev =>
                            prev.includes(campaign.id)
                              ? prev.filter(id => id !== campaign.id)
                              : [...prev, campaign.id]
                          );
                        }}
                      >
                        <span className="text-sm">{campaign.name}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
          </div>
        )}

        {activeTab === 'call' && (
          <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Label>Click-to-Call</Label>
                  <p className="text-xs text-muted-foreground">Permetti agli utenti di richiedere una chiamata</p>
                </div>
                <Switch
                  checked={formData.click_to_call_enabled}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, click_to_call_enabled: checked }))}
                />
              </div>

              {formData.click_to_call_enabled && (
                <>
                  <div className="space-y-2">
                    <Label>Tipo Flusso</Label>
                    <p className="text-xs text-muted-foreground">Scegli chi gestisce la chiamata al cliente</p>
                    <Select
                      value={formData.click_to_call_config.flow_type || 'human'}
                      onValueChange={(value: 'human' | 'ai_agent') => setFormData(prev => ({
                        ...prev,
                        click_to_call_config: { ...prev.click_to_call_config, flow_type: value }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="human">Operatore (Coda / Interno)</SelectItem>
                        <SelectItem value="ai_agent">AI Voice Agent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {(formData.click_to_call_config.flow_type || 'human') === 'human' && (
                    <>
                      <div className="space-y-2">
                        <Label>Modalità</Label>
                        <Select
                          value={formData.click_to_call_config.mode || 'callback'}
                          onValueChange={(value: 'webrtc' | 'callback') => setFormData(prev => ({
                            ...prev,
                            click_to_call_config: { ...prev.click_to_call_config, mode: value }
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="callback">Callback (Ti richiamiamo)</SelectItem>
                            <SelectItem value="webrtc">WebRTC (Chiamata dal browser)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Destinazione</Label>
                        <Select
                          value={formData.click_to_call_config.destination_type || 'queue'}
                          onValueChange={(value: 'extension' | 'queue') => setFormData(prev => ({
                            ...prev,
                            click_to_call_config: { ...prev.click_to_call_config, destination_type: value }
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="queue">Coda</SelectItem>
                            <SelectItem value="extension">Interno</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>
                          {formData.click_to_call_config.destination_type === 'queue' ? 'Seleziona Coda' : 'Seleziona Interno'}
                        </Label>
                        <Select
                          value={formData.click_to_call_config.destination_id || ''}
                          onValueChange={(value) => setFormData(prev => ({
                            ...prev,
                            click_to_call_config: { ...prev.click_to_call_config, destination_id: value }
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona..." />
                          </SelectTrigger>
                          <SelectContent>
                            {formData.click_to_call_config.destination_type === 'queue'
                              ? queues.filter((q) => q.id).map((q) => (
                                  <SelectItem key={q.id} value={q.id}>{q.name} ({q.extension})</SelectItem>
                                ))
                              : extensions.filter((e) => e.id).map((e) => (
                                  <SelectItem key={e.id} value={e.id}>{e.display_name} ({e.extension_number})</SelectItem>
                                ))
                            }
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {(formData.click_to_call_config.flow_type) === 'ai_agent' && (
                    <div className="space-y-2">
                      <Label>Agent AI Voice</Label>
                      <p className="text-xs text-muted-foreground">L'agente AI chiamerà direttamente il cliente</p>
                      <Select
                        value={formData.click_to_call_config.ai_agent_id || ''}
                        onValueChange={(value) => setFormData(prev => ({
                          ...prev,
                          click_to_call_config: { ...prev.click_to_call_config, ai_agent_id: value }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona agente AI..." />
                        </SelectTrigger>
                        <SelectContent>
                          {voiceAgents.filter(a => a.is_active !== false && a.sip_enabled === true).map((a) => (
                            <SelectItem key={a.id} value={a.id}>
                              <span className="flex items-center gap-2">
                                <span>{a.name}</span>
                                {(a.provider_name || a.provider) && (
                                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-700 text-muted-foreground">
                                    {a.provider_name || a.provider}
                                  </span>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {voiceAgents.filter(a => a.is_active !== false && a.sip_enabled === true).length === 0 && (
                        <p className="text-xs text-amber-500">Nessun agente AI Voice SIP configurato. Vai in AI Tools per crearne uno.</p>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Trunk SIP (obbligatorio)</Label>
                    <p className="text-xs text-muted-foreground">Gateway utilizzato per le chiamate in uscita</p>
                    <Select
                      value={formData.click_to_call_config.trunk_id || ''}
                      onValueChange={(value) => setFormData(prev => ({
                        ...prev,
                        click_to_call_config: { ...prev.click_to_call_config, trunk_id: value }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona trunk..." />
                      </SelectTrigger>
                      <SelectContent>
                        {trunks.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!formData.click_to_call_config.trunk_id && formData.click_to_call_enabled && (
                      <p className="text-xs text-red-400">Seleziona un trunk per abilitare le chiamate</p>
                    )}
                  </div>
                </>
              )}
          </div>
        )}

        {activeTab === 'hours' && (
          <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Label>Sempre Disponibile</Label>
                  <p className="text-xs text-muted-foreground">Il chatbot risponde 24/7</p>
                </div>
                <Switch
                  checked={formData.availability_config.always_available}
                  onCheckedChange={(checked) => setFormData(prev => ({
                    ...prev,
                    availability_config: { ...prev.availability_config, always_available: checked }
                  }))}
                />
              </div>

              {!formData.availability_config.always_available && (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Configurazione orari personalizzati in arrivo</p>
                  <p className="text-xs mt-1">Per ora, il chatbot è sempre attivo</p>
                </div>
              )}
          </div>
        )}
      </div>

      <KbModelChangeGuardDialog
        state={kbGuard.state}
        primaryButtonClassName="bg-gradient-to-r from-pink-500 to-rose-600"
      />
    </AgentConfigShell>
  );
}
